import { useParams, useNavigate } from "react-router-dom"
import { useEffect, useState, useMemo } from "react"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Input,
    Select,
    SelectItem,
    Textarea,
    Checkbox,
    Skeleton,
} from "@heroui/react"
import {
    ChevronLeft,
    Save,
    BookOpen,
    FileText,
    Sparkles,
} from "lucide-react"
import { useGetPascoById, useUpdatePasco, useExtractPascoMetadata } from "@src/presentation/hooks/pasco"
import { useUniversities } from "@src/presentation/hooks/universities"
import { ToastService } from "@src/shared/services/toast.service"
import type { UpdatePascoDTO } from "@src/application/pascos/dto"

export default function EditPascoPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    // Fetch PASCO data
    const { data: pasco, isLoading, isError, error } = useGetPascoById(id!)

    // Fetch universities for the dropdown
    const { data: universitiesData } = useUniversities()

    // Update mutation
    const { mutate: updatePasco, isPending: isUpdating } = useUpdatePasco({
        onSuccess: () => {
            ToastService.success("PASCO updated successfully")
            navigate(`/pascos/${id}`)
        },
        onError: (error: any) => {
            ToastService.error(error?.message || "Failed to update PASCO")
        },
    })

    // Extract metadata mutation
    const { mutate: extractMetadata, isPending: isExtracting } = useExtractPascoMetadata({
        onSuccess: (metadata) => {
            ToastService.success(metadata.message || "Metadata extracted successfully")
            // Populate form with extracted metadata
            setFormData((prev) => ({
                ...prev,
                courseCode: metadata.courseCode || prev.courseCode,
                courseName: metadata.courseName || prev.courseName,
                academicYear: metadata.academicYear || prev.academicYear,
                semester: metadata.semester?.toString() || prev.semester,
                level: metadata.level?.toString() || prev.level,
                isInterimAssessment: metadata.isInterimAssessment,
                isSupplementary: metadata.isSupplementary,
            }))
        },
        onError: (error: any) => {
            ToastService.error(error?.message || "Failed to extract metadata")
        },
    })

    // Form state
    const [formData, setFormData] = useState({
        courseCode: "",
        courseName: "",
        academicYear: "",
        semester: "",
        level: "",
        courseDescription: "",
        department: "",
        isInterimAssessment: false,
        isSupplementary: false,
        universityId: "",
    })

    // Check if metadata is missing (PASCO needs AI extraction)
    const isMissingMetadata = useMemo(() => {
        if (!pasco) return false
        return !pasco.courseCode || !pasco.courseName || !pasco.academicYear || !pasco.semester
    }, [pasco])

    // Handle extract metadata
    const handleExtractMetadata = () => {
        if (!id) return
        extractMetadata(id)
    }

    // Populate form when PASCO data loads
    useEffect(() => {
        if (pasco) {
            setFormData({
                courseCode: pasco.courseCode || "",
                courseName: pasco.courseName || "",
                academicYear: pasco.academicYear || "",
                semester: pasco.semester?.toString() || "",
                level: pasco.level?.toString() || "",
                courseDescription: pasco.courseDescription || "",
                department: pasco.department || "",
                isInterimAssessment: pasco.isInterimAssessment || false,
                isSupplementary: pasco.isSupplementary || false,
                universityId: pasco.universityId || "",
            })
        }
    }, [pasco])

    // Handle input change
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target
        // Trim academic year to prevent whitespace issues
        const processedValue = name === 'academicYear' ? value.trim() : value
        setFormData((prev) => ({ ...prev, [name]: processedValue }))
    }

    // Handle submit
    const handleSubmit = () => {
        if (!id) return

        // Validate required fields
        if (!formData.courseCode.trim()) {
            ToastService.error("Course Code is required")
            return
        }
        if (!formData.courseName.trim()) {
            ToastService.error("Course Name is required")
            return
        }
        if (!formData.academicYear.trim()) {
            ToastService.error("Academic Year is required")
            return
        }
        if (!formData.semester) {
            ToastService.error("Semester is required")
            return
        }
        if (!formData.universityId) {
            ToastService.error("University is required")
            return
        }

        const updateData: UpdatePascoDTO = {
            id,
            courseCode: formData.courseCode.trim(),
            courseName: formData.courseName.trim(),
            academicYear: formData.academicYear.trim(),
            semester: parseInt(formData.semester),
            level: formData.level ? parseInt(formData.level) : null,
            courseDescription: formData.courseDescription.trim() || null,
            department: formData.department.trim() || null,
            isInterimAssessment: formData.isInterimAssessment,
            isSupplementary: formData.isSupplementary,
            universityId: formData.universityId,
        }

        updatePasco(updateData)
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
                {/* Header skeleton */}
                <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <Skeleton className="h-8 w-20 rounded" />
                                <Skeleton className="h-6 w-8 rounded-lg" />
                                <Skeleton className="h-6 w-32 rounded" />
                            </div>
                            <Skeleton className="h-9 w-24 rounded" />
                        </div>
                    </div>
                </header>

                {/* Main content skeleton */}
                <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <Card className="max-w-4xl mx-auto border border-border rounded shadow-none">
                        <CardHeader className="border-b border-border p-6">
                            <div className="flex items-center gap-3">
                                <Skeleton className="h-8 w-8 rounded-lg" />
                                <Skeleton className="h-6 w-32 rounded" />
                            </div>
                        </CardHeader>
                        <CardBody className="p-6 space-y-6">
                            {/* Form skeleton */}
                            <div className="space-y-4">
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} className="space-y-2">
                                        <Skeleton className="h-4 w-24 rounded" />
                                        <Skeleton className="h-10 w-full rounded" />
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-3">
                                <Skeleton className="h-5 w-5 rounded" />
                                <Skeleton className="h-4 w-48 rounded" />
                            </div>
                        </CardBody>
                    </Card>
                </main>
            </div>
        )
    }

    // Error state
    if (isError || !pasco) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <Card className="max-w-md rounded border border-border shadow-none">
                    <CardBody className="text-center">
                        <div className="mb-4 flex justify-center">
                            <div className="rounded-full bg-danger/10 p-3">
                                <FileText className="h-8 w-8 text-danger" />
                            </div>
                        </div>
                        <h3 className="mb-2 text-lg font-semibold">PASCO Not Found</h3>
                        <p className="mb-4 text-sm text-foreground-500">
                            {error?.message || "The PASCO you're looking for doesn't exist or has been deleted."}
                        </p>
                        <Button
                            color="primary"
                            onPress={() => navigate("/pascos")}
                            startContent={<ChevronLeft className="h-4 w-4" />}
                        >
                            Back to PASCOs
                        </Button>
                    </CardBody>
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <BookOpen className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Edit PASCO</h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Button
                                variant="flat"
                                onPress={() => navigate(`/pascos/${id}`)}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Cancel</span>
                                <span className="sm:hidden">Cancel</span>
                            </Button>
                            <Button
                                color="primary"
                                startContent={<Save className="h-4 w-4" />}
                                onPress={handleSubmit}
                                isLoading={isUpdating}
                                className="rounded flex-1 sm:flex-none"
                                size="sm"
                            >
                                <span className="hidden sm:inline">Save Changes</span>
                                <span className="sm:hidden">Save</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
                {/* Missing Metadata Banner */}
                {isMissingMetadata && (
                    <Card className="rounded border border-warning/50 bg-warning/5 shadow-none mb-6">
                        <CardBody className="p-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg bg-warning/10 flex-shrink-0">
                                        <Sparkles className="h-5 w-5 text-warning" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-warning-700 dark:text-warning">Missing Metadata</p>
                                        <p className="text-sm text-foreground-600 mt-0.5">
                                            This PASCO is missing course information. Use AI to automatically extract metadata from the document.
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    color="warning"
                                    variant="flat"
                                    startContent={<Sparkles className="h-4 w-4" />}
                                    onPress={handleExtractMetadata}
                                    isLoading={isExtracting}
                                    className="rounded flex-shrink-0"
                                    size="sm"
                                >
                                    {isExtracting ? "Extracting..." : "Extract with AI"}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Edit Form */}
                <Card className="rounded border border-border shadow-none">
                    <CardHeader className="border-b border-border bg-default-50">
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                                <BookOpen className="h-5 w-5 text-primary" />
                                <h2 className="text-lg font-semibold">Course Information</h2>
                            </div>
                            {!isMissingMetadata && (
                                <Button
                                    variant="light"
                                    size="sm"
                                    startContent={<Sparkles className="h-4 w-4" />}
                                    onPress={handleExtractMetadata}
                                    isLoading={isExtracting}
                                    className="rounded text-default-600 hover:text-primary"
                                >
                                    {isExtracting ? "Extracting..." : "Re-extract with AI"}
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardBody className="space-y-6 p-6">
                        {/* Course Code and Name */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Input
                                label="Course Code"
                                name="courseCode"
                                value={formData.courseCode}
                                onChange={handleInputChange}
                                isRequired
                                placeholder="e.g., CS101"
                                classNames={{
                                    label: "text-foreground font-medium",
                                    inputWrapper: "rounded border border-border",
                                }}
                            />
                            <Input
                                label="Course Name"
                                name="courseName"
                                value={formData.courseName}
                                onChange={handleInputChange}
                                isRequired
                                placeholder="e.g., Introduction to Computer Science"
                                classNames={{
                                    label: "text-foreground font-medium",
                                    inputWrapper: "rounded border border-border",
                                }}
                            />
                        </div>

                        {/* Academic Details */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <Input
                                label="Academic Year"
                                name="academicYear"
                                value={formData.academicYear}
                                onChange={handleInputChange}
                                isRequired
                                placeholder="e.g., 2024/2025"
                                classNames={{
                                    label: "text-foreground font-medium",
                                    inputWrapper: "rounded border border-border",
                                }}
                            />
                            <Select
                                label="Semester"
                                name="semester"
                                selectedKeys={formData.semester ? [formData.semester] : []}
                                onChange={(e) => handleInputChange(e as any)}
                                isRequired
                                classNames={{
                                    label: "text-foreground font-medium",
                                    trigger: "rounded border border-border",
                                    popoverContent: "rounded",
                                }}
                            >
                                <SelectItem key="1" className="rounded">
                                    1st Semester
                                </SelectItem>
                                <SelectItem key="2" className="rounded">
                                    2nd Semester
                                </SelectItem>
                                <SelectItem key="3" className="rounded">
                                    3rd Semester
                                </SelectItem>
                            </Select>
                            <Input
                                type="number"
                                label="Level"
                                name="level"
                                value={formData.level}
                                onChange={handleInputChange}
                                placeholder="e.g., 100, 200, 500, 600"
                                description="Enter course level (100-600+)"
                                classNames={{
                                    label: "text-foreground font-medium",
                                    inputWrapper: "rounded border border-border",
                                }}
                            />
                        </div>

                        {/* Department (Optional) */}
                        <Input
                            label="Department"
                            name="department"
                            value={formData.department}
                            onChange={handleInputChange}
                            placeholder="e.g., Computer Science"
                            description="Optional: Specify the department offering this course"
                            classNames={{
                                label: "text-foreground font-medium",
                                inputWrapper: "rounded border border-border",
                            }}
                        />

                        {/* University */}
                        <Select
                            label="University"
                            name="universityId"
                            selectedKeys={formData.universityId ? new Set([formData.universityId]) : new Set()}
                            onSelectionChange={(keys) => {
                                const selected = Array.from(keys)[0] as string;
                                setFormData((prev) => ({ ...prev, universityId: selected || "" }));
                            }}
                            placeholder="Select a university"
                            isRequired
                            classNames={{
                                label: "text-foreground font-medium",
                                trigger: "rounded border border-border",
                                popoverContent: "rounded",
                            }}
                        >
                            {universitiesData?.universities?.map((university) => (
                                <SelectItem key={university.university_id} className="rounded">
                                    {university.name}
                                </SelectItem>
                            )) || []}
                        </Select>

                        {/* Course Description */}
                        <Textarea
                            label="Course Description"
                            name="courseDescription"
                            value={formData.courseDescription}
                            onChange={handleInputChange}
                            placeholder="Enter a brief description of the course content and objectives..."
                            description="Optional: Provide additional context about the course"
                            minRows={4}
                            classNames={{
                                label: "text-foreground font-medium",
                                inputWrapper: "rounded border border-border",
                            }}
                        />

                        {/* Interim Assessment Checkbox */}
                        <div className="rounded border border-border bg-default-100 p-4">
                            <Checkbox
                                isSelected={formData.isInterimAssessment}
                                onValueChange={(checked) =>
                                    setFormData((prev) => ({ ...prev, isInterimAssessment: checked }))
                                }
                            >
                                <div>
                                    <p className="font-medium">Interim Assessment</p>
                                    <p className="text-sm text-foreground-500">
                                        Check this if this PASCO is for an interim assessment (mid-semester exam)
                                    </p>
                                </div>
                            </Checkbox>
                        </div>

                        {/* Supplementary Checkbox */}
                        <div className="rounded border border-border bg-default-100 p-4">
                            <Checkbox
                                isSelected={formData.isSupplementary}
                                onValueChange={(checked) =>
                                    setFormData((prev) => ({ ...prev, isSupplementary: checked }))
                                }
                            >
                                <div>
                                    <p className="font-medium">Supplementary</p>
                                    <p className="text-sm text-foreground-500">
                                        Check this if this PASCO is supplementary material
                                    </p>
                                </div>
                            </Checkbox>
                        </div>

                        {/* Info Message */}
                        <div className="rounded border border-primary/20 bg-primary/5 p-4">
                            <div className="flex gap-3">
                                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                                <div className="text-sm">
                                    <p className="font-medium text-primary">Note about file updates</p>
                                    <p className="mt-1 text-foreground-600">
                                        You cannot change the PASCO file here. If you need to update the file, please create a new PASCO entry.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </main>
        </div>
    )
}
